import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, BRANCH_KEY, syncActiveBranch } from "@/lib/api";

const TenantContext = createContext(null);

// Convert #RRGGBB to a slightly darker hover shade
function shade(hex, amount = -0.1) {
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * amount)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * amount)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export function TenantProvider({ children }) {
  const { session, refresh } = useAuth();
  const isSuperAdmin = session?.user?.is_super_admin;

  // Active tenant: for tenant users = their tenant. For super admin = optional "as" tenant.
  const [activeTenantId, setActiveTenantId] = useState(
    () => localStorage.getItem("talleros_active_tenant_id") || null
  );
  const [activeTenant, setActiveTenant] = useState(session?.tenant || null);
  const [branches, setBranches] = useState(session?.branches || []);
  const [activeBranchId, setActiveBranchId] = useState(
    () => localStorage.getItem("talleros_active_branch_id") || null
  );

  // Sync when session changes
  useEffect(() => {
    if (!session) {
      setActiveTenant(null);
      setBranches([]);
      return;
    }
    if (!isSuperAdmin) {
      setActiveTenant(session.tenant);
      setBranches(session.branches || []);
      setActiveTenantId(session.tenant?.id || null);
      setActiveBranchId(syncActiveBranch(session.branches, session.user?.branch_id));
    } else {
      // super admin — load their impersonated tenant if any
      setBranches([]);
      if (!activeTenantId) localStorage.removeItem(BRANCH_KEY);
      if (activeTenantId) {
        void loadTenant(activeTenantId);
      } else {
        setActiveTenant(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const loadTenant = useCallback(async (tenantId) => {
    if (!tenantId) return;
    try {
      const [{ data: t }, { data: bs }] = await Promise.all([
        api.get(`/tenants/${tenantId}`),
        api.get(`/branches`, { params: { tenant_id: tenantId } }),
      ]);
      setActiveTenant(t);
      setBranches(bs || []);
      setActiveBranchId(syncActiveBranch(bs));
    } catch { /* noop */ }
  }, []);

  const selectTenant = useCallback(
    async (tenantId) => {
      setActiveTenantId(tenantId);
      if (tenantId) localStorage.setItem("talleros_active_tenant_id", tenantId);
      else localStorage.removeItem("talleros_active_tenant_id");
      if (tenantId) await loadTenant(tenantId);
      else {
        setActiveTenant(null);
        setBranches([]);
        setActiveBranchId(null);
        localStorage.removeItem(BRANCH_KEY);
      }
    },
    [loadTenant]
  );

  const selectBranch = useCallback((branchId) => {
    if (!branchId || branchId === localStorage.getItem(BRANCH_KEY)) return;
    localStorage.setItem(BRANCH_KEY, branchId);
    // Reload so every screen refetches data for the new active branch.
    window.location.reload();
  }, []);

  // Inject tenant primary color as CSS variable
  useEffect(() => {
    const root = document.documentElement;
    const color = activeTenant?.primary_color || "#0F172A";
    root.style.setProperty("--tenant-primary", color);
    root.style.setProperty("--tenant-primary-hover", shade(color, -0.08));
    root.style.setProperty("--tenant-primary-contrast", "#ffffff");
  }, [activeTenant]);

  const activeBranch = useMemo(
    () => branches.find((b) => b.id === activeBranchId) || null,
    [branches, activeBranchId]
  );

  const refreshTenant = useCallback(async () => {
    if (isSuperAdmin) {
      if (activeTenantId) await loadTenant(activeTenantId);
    } else {
      await refresh();
    }
  }, [isSuperAdmin, activeTenantId, loadTenant, refresh]);

  const value = useMemo(
    () => ({
      isSuperAdmin,
      activeTenantId,
      activeTenant,
      branches: activeBranch ? [activeBranch] : [],
      authorizedBranches: branches,
      activeBranchId,
      activeBranch,
      selectTenant,
      selectBranch,
      refreshTenant,
      // helper to pass to API when acting as super admin
      tenantQueryParam: isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {},
    }),
    [
      isSuperAdmin,
      activeTenantId,
      activeTenant,
      branches,
      activeBranchId,
      activeBranch,
      selectTenant,
      selectBranch,
      refreshTenant,
    ]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be inside TenantProvider");
  return ctx;
}
